/**
 * CostSummaryCard Component
 *
 * Displays cost metrics with budget utilization visualization
 * Follows: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { Card } from 'flowbite-react';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@heroicons/react/24/solid';
import { formatCurrency, getUtilizationColor } from '../api/costs';

export interface CostSummaryCardProps {
  title: string;
  totalCost: number;
  budgetCost: number;
  utilizationPct: number;
  executionCount: number;
  trend?: {
    previousCost: number;
    changePercentage: number;
    direction: 'up' | 'down' | 'stable';
  };
  isLoading?: boolean;
  onClick?: () => void;
}

export const CostSummaryCard: React.FC<CostSummaryCardProps> = ({
  title,
  totalCost,
  budgetCost,
  utilizationPct,
  executionCount,
  trend,
  isLoading = false,
  onClick
}) => {
  // Determine status color based on utilization
  const statusColor = getUtilizationColor(utilizationPct) as 'green' | 'yellow' | 'red';

  const statusColorClasses = {
    green: 'border-green-500 bg-green-50',
    yellow: 'border-yellow-500 bg-yellow-50',
    red: 'border-red-500 bg-red-50'
  };

  const badgeColorClasses = {
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800'
  };

  const progressBarClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500'
  };

  // Trend icon
  const TrendIcon = trend?.direction === 'up' ? ArrowUpIcon : trend?.direction === 'down' ? ArrowDownIcon : MinusIcon;
  const trendColorClass = trend?.direction === 'up' ? 'text-red-500' : trend?.direction === 'down' ? 'text-green-500' : 'text-gray-500';

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <div className="space-y-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 rounded w-full"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={`border-l-4 transition-all duration-200 hover:shadow-lg ${statusColorClasses[statusColor]} ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-start">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${badgeColorClasses[statusColor]}`}>
            {utilizationPct.toFixed(1)}%
          </span>
        </div>

        {/* Total Cost */}
        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(totalCost)}
            </p>
            {trend && (
              <div className={`flex items-center text-sm font-medium ${trendColorClass}`}>
                <TrendIcon className="w-4 h-4 mr-1" />
                <span>{Math.abs(trend.changePercentage).toFixed(1)}%</span>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-600 mt-1">
            of {formatCurrency(budgetCost)} budget
          </p>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all duration-300 ${progressBarClasses[statusColor]}`}
              style={{ width: `${Math.min(utilizationPct, 100)}%` }}
            ></div>
          </div>

          {/* Budget Status Text */}
          <div className="flex justify-between text-xs text-gray-600">
            <span>
              {utilizationPct < 80 && 'Under budget'}
              {utilizationPct >= 80 && utilizationPct <= 100 && 'Approaching budget'}
              {utilizationPct > 100 && `Over budget by ${formatCurrency(totalCost - budgetCost)}`}
            </span>
            <span>{executionCount.toLocaleString()} executions</span>
          </div>
        </div>

        {/* Trend Details */}
        {trend && (
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-600">
              vs. previous period: {formatCurrency(trend.previousCost)}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default CostSummaryCard;
